// #region Imports
import { useState, useEffect, useCallback } from 'react';
import caixaApi from '../services/caixaApi';
import { caixaFromBackend, movimentoFromBackend, computeCaixaStats } from '../pages/Caixa/caixa-utils';
// #endregion

export default function useCaixa() {
  // #region Estado
  const [caixas, setCaixas] = useState([]);
  const [movimentos, setMovimentos] = useState([]); // movimentos do caixa selecionado
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // #endregion

  // #region Efeito carga inicial
  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const raw = await caixaApi.listCaixas();
        if (!active) return;
        setCaixas(raw.map(caixaFromBackend));
      } catch (e) {
        if (active) setError(e);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);
  // #endregion

  // #region Listar
  const listar = useCallback(({ q = '', page = 1, pageSize = 10, status = 'TODOS' } = {}) => {
    let filtered = [...caixas];
    const norm = q.trim().toLowerCase();

    if (norm) {
      filtered = filtered.filter(c => (
        (c.data_referencia || '').includes(norm) ||
        String(c.id).includes(norm) ||
        (c.observacoes || '').toLowerCase().includes(norm)
      ));
    }

    if (status !== 'TODOS') {
      filtered = filtered.filter(c => c.status === status);
    }

    const total = filtered.length;
    const start = (page - 1) * pageSize;
    const data = filtered.slice(start, start + pageSize);
    return { data, total, page, pageSize };
  }, [caixas]);
  // #endregion

  // #region Obter
  const obter = useCallback(id => caixas.find(c => c.id === id) || null, [caixas]);
  // #endregion

  // #region Criar/Fechar
  const abrirCaixa = useCallback(async payload => {
    const dto = await caixaApi.createCaixa(payload);
    const inst = caixaFromBackend(dto);
    setCaixas(prev => [inst, ...prev]);
    return inst;
  }, []);

  const fechar = useCallback(async (id, { valor_real, manual = true } = {}) => {
    const dto = await caixaApi.fecharCaixa(id, { valor_real, manual });
    if (!dto) return null;
    const inst = caixaFromBackend(dto);
    setCaixas(prev => prev.map(c => (c.id === id ? inst : c)));
    return inst;
  }, []);
  // #endregion

  // #region Movimentos
  const carregarMovimentos = useCallback(async caixaId => {
    const raw = await caixaApi.listMovimentosByCaixa(caixaId);
    setMovimentos(raw.map(movimentoFromBackend));
  }, []);

  const adicionarMovimento = useCallback(async (caixaId, payload) => {
    const dto = await caixaApi.addMovimento(caixaId, payload);
    const inst = movimentoFromBackend(dto);
    setMovimentos(prev => [inst, ...prev]);
    setCaixas(prev => prev.map(c => (c.id === caixaId ? {
      ...c,
      ...(payload.tipo === 'ENTRADA'
        ? { total_entradas: (c.total_entradas || 0) + payload.valor }
        : { total_saidas: (c.total_saidas || 0) + payload.valor })
    } : c)));
    return inst;
  }, []);
  // #endregion

  // #region Stats
  const stats = computeCaixaStats(caixas);
  // #endregion

  // #region Export
  return {
    caixas,
    movimentos,
    stats,
    loading,
    error,
    listar,
    obter,
    abrirCaixa,
    fechar,
    carregarMovimentos,
    adicionarMovimento,
  };
  // #endregion
}
